import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Button,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  SettingsBackupRestore as SettingsBackupRestoreIcon,
  PeopleAlt as PeopleAltIcon,
  Lightbulb as LightbulbIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ServiceRequestForm from './service-request-form.component';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const getTheme = (mode) => ({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#6a1bff' : '#5e00ff'
    },
    secondary: {
      main: mode === 'dark' ? '#00e5ff' : '#00b4cc'
    },
    background: {
      default: mode === 'dark' ? '#121212' : '#f5f7ff',
      paper: mode === 'dark' ? '#1a1a1a' : '#ffffff'
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#000000',
      secondary: mode === 'dark' ? '#b0b0b0' : '#555555'
    }
  }
});

const ServiceRequest = ({ mode = 'light' }) => {
  const theme = getTheme(mode);

  // Benefits of working with Soranova
  const benefits = [
    {
      icon: <LightbulbIcon fontSize="large" />,
      title: 'Innovative Solutions',
      description: 'We leverage cutting-edge technologies and methodologies to create innovative, future-proof solutions.'
    },
    {
      icon: <PeopleAltIcon fontSize="large" />,
      title: 'Expert Team',
      description: 'Our team of experienced developers, designers, and consultants bring extensive knowledge to every project.'
    },
    {
      icon: <AccessTimeIcon fontSize="large" />,
      title: 'Timely Delivery',
      description: 'We prioritize meeting deadlines and maintaining transparent communication throughout the project.'
    },
    {
      icon: <AttachMoneyIcon fontSize="large" />,
      title: 'Cost-Effective',
      description: 'Our solutions provide excellent value with optimized development processes and efficient resource allocation.'
    },
    {
      icon: <SettingsBackupRestoreIcon fontSize="large" />,
      title: 'Ongoing Support',
      description: "We don't just deliver and disappear. We provide continuous support and maintenance for all our solutions."
    },
    {
      icon: <CheckCircleIcon fontSize="large" />,
      title: 'Quality Assurance',
      description: 'Every solution we develop undergoes rigorous testing to ensure reliability, security, and performance.'
    }
  ];

  return (
    <Box sx={{
      bgcolor: 'background.default',
      color: 'text.primary',
      minHeight: '100vh',
    }}>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 10 },
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(10, 10, 26, 0.95) 0%, rgba(26, 26, 46, 0.95) 100%)'
            : 'linear-gradient(135deg, rgba(245, 247, 255, 0.95) 0%, rgba(230, 235, 255, 0.95) 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: 'secondary.main',
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    mb: 2,
                  }}
                >
                  REQUEST OUR SERVICES
                </Typography>
                <Typography
                  variant="h1"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    mb: 3,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Let's Start Your Digital Journey
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  paragraph
                  sx={{ mb: 4 }}
                >
                  Share your project details, and our experts will craft a tailored solution to bring your vision to life.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  href="#request-form"
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: '50px',
                    boxShadow: '0 8px 20px rgba(94, 0, 255, 0.3)',
                  }}
                >
                  Submit Your Request
                </Button>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                <Box
                  component="img"
                  src="https://source.unsplash.com/random/800x600/?technology,collaboration"
                  alt="Service Request"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInUp}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              >
                OUR PROCESS
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                How It Works
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto' }}
              >
                From project initiation to delivery, we maintain transparency and collaboration throughout our streamlined process.
              </Typography>
            </motion.div>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(94, 0, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                      }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Submit Request
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Fill out the service request form with details about your project requirements, timeline, and budget.
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: 0.2 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(94, 0, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                      }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Consultation
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Our team will review your request and schedule a consultation to discuss your project in more detail.
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: 0.3 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      bgcolor: 'rgba(94, 0, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      mx: 'auto',
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                      }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    Proposal & Development
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    We'll provide a detailed proposal and, once approved, begin the development process with regular updates.
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : 'rgba(245, 247, 255, 0.8)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={fadeInUp}
              >
                <Typography
                  variant="overline"
                  sx={{
                    display: 'block',
                    color: 'primary.main',
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    mb: 2,
                  }}
                >
                  WHY CHOOSE US
                </Typography>
                <Typography
                  variant="h2"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  Benefits of Working with Soranova
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  paragraph
                  sx={{ mb: 4 }}
                >
                  Partner with us for innovative solutions tailored to your business needs, delivered by experts committed to your success.
                </Typography>
                <Box
                  component="img"
                  src="https://source.unsplash.com/random/600x400/?innovation,technology"
                  alt="Innovation"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 4,
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    display: { xs: 'none', md: 'block' }
                  }}
                />
              </motion.div>
            </Grid>
            <Grid item xs={12} md={7}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={staggerContainer}
              >
                <Grid container spacing={3}>
                  {benefits.map((benefit, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <motion.div variants={fadeInUp}>
                        <Card
                          sx={{
                            height: '100%',
                            boxShadow: 'none',
                            bgcolor: 'background.paper',
                            borderRadius: 4,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar
                                sx={{
                                  bgcolor: 'primary.main',
                                  mr: 2,
                                }}
                              >
                                {benefit.icon}
                              </Avatar>
                              <Typography variant="h6" component="h3">
                                {benefit.title}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {benefit.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Request Form Section */}
      <Box id="request-form" sx={{ py: { xs: 8, md: 12 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={fadeInUp}
            >
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              >
                GET STARTED
              </Typography>
              <Typography
                variant="h2"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                Request Our Services
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 700, mx: 'auto' }}
              >
                Complete the form below to tell us about your project, and we'll get back to you with a customized solution.
              </Typography>
            </motion.div>
          </Box>

          <ServiceRequestForm mode={mode} />
        </Container>
      </Box>
    </Box>
  );
};

export default ServiceRequest;